import React, { ChangeEvent, KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react'

import { clearNumberFormatter } from 'aem-libs-react-ts/src/utils/Formatters'
import { IUserTracking } from '../../services/UserTracking'

export interface IProps {
  inputClass?: string
  id?: string
  name?: string
  unit?: string
  min?: number
  max?: number
  defaultValue?: number
  value: number
  decimals?: number
  errorClass?: string
  invalid?: boolean
  errorElement?: any
  disabled?: boolean
  rf?: string
  trackElement?: string
  userTracking?: IUserTracking
  format?: (value: string) => void
  onChange?: (value: any) => void
  onChangeComplete?: (value: number) => void
}

/**
 * Float input
 *
 *
 * @param inputClass (string, optional) - Css input class
 * @param id (string, optional) - Input id
 * @param name (string, optional) - Input name
 * @param value (number, required) - Contolled input value
 * @param defaultValue (number, optional) - Default value.
 * @param unit (string, optional) - Unit in input
 * @param min (number, optional) - Min input value
 * @param max (number, optional) - Max input value
 * @param disabled (boolean, optional) - Enable / disable input
 * @param invalid (boolean, optional) - Indicates if the current value is (in)valid
 * @param errorElement (any, optional) - Element or string which is visible when `invalid` param is set to true
 * @param errorClass (string, optional) - Input class when `invalid` param is set to true
 * @param decimals (number, optional) - Format value to assigned decimals number
 * @param onChange (void: number, optional) - On change handler
 * @param onChangeComplete (void: number, optional) - On complete change input change handler (on input blur, enter)
 * @param userTracking (string, optional) - User tracking service
 * @param trackElement (string, optional) - User tracking track element id
 * @param rf (string, optional) - Optional tracking id fro automated tests
 *
 */
export default function FloatInput({
  inputClass,
  id,
  name,
  unit,
  min,
  max,
  defaultValue,
  value,
  decimals,
  errorClass,
  invalid,
  errorElement,
  disabled,
  rf,
  trackElement,
  userTracking,
  format,
  onChange,
  onChangeComplete
}: IProps) {
  const defaultDecimals = decimals ? decimals : 0

  const formatValue = (val: string | number): string => (format ? format.call(this, val) : val)

  const toFixedNoRound = (val: number): string => {
    if (!decimals) {
      return val.toString()
    }
    const base = Math.pow(10, defaultDecimals)
    return (Math.floor(val * base) / base).toFixed(defaultDecimals)
  }

  const isDefined = (val: number) => val !== undefined && val !== null

  const transformValueToString = (newValue: number) => {
    if (!isDefined(newValue)) {
      return
    }

    const stringValue = toFixedNoRound(newValue)
    return formatValue(stringValue).replace('.', ',')
  }

  const transformValueToFloat = (newValue: string) => {
    if (!newValue) {
      return
    }

    return parseFloat(clearNumberFormatter(newValue).replace(',', '.'))
  }

  const finalDefault = defaultValue ? toFixedNoRound(defaultValue).replace('.', ',') : '0'

  const [currentValue, setCurrentValue] = useState(formatValue(finalDefault))
  const [lastValue, setLastValue] = useState(formatValue(finalDefault))
  const numberRef: RefObject<HTMLInputElement> = useRef()

  useEffect(() => {
    if (!isDefined(value)) {
      return
    }
    const stringValue = transformValueToString(value)
    if (stringValue !== currentValue) {
      setCurrentValue(stringValue)
    }
  }, [value])

  const finalInputClass = invalid && errorClass ? errorClass : inputClass

  const getDefaultValue = (): number => (defaultValue ? defaultValue : 0)

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      numberRef.current.blur()
      validateAndComplete()
    }
  }

  const trackChange = () => {
    if (userTracking && trackElement) {
      userTracking.onInputChange(trackElement)
    }
  }

  const handleOutsideClick = () => validateAndComplete()

  const validateValue = (newValue: number) => {
    let validatedValue: number = newValue
    if (!isNaN(newValue)) {
      if (min !== undefined && min !== null && newValue < min) {
        validatedValue = min
      }

      if (max !== undefined && max !== null && newValue > max) {
        validatedValue = max
      }
    } else {
      validatedValue = getDefaultValue()
    }

    return validatedValue
  }

  const validateAndComplete = () => {
    if (numberRef.current.value === '' && !defaultValue) {
      return
    }

    if (currentValue === lastValue) {
      return
    }

    let floatValue = transformValueToFloat(currentValue)
    floatValue = validateValue(floatValue)

    const stringValue = transformValueToString(floatValue)
    setCurrentValue(stringValue)
    setLastValue(stringValue)
    trackChange()

    if (onChangeComplete) {
      onChangeComplete(floatValue)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue: string = e.target.value
    setCurrentValue(newValue)
    if (onChange) {
      const floatValue = transformValueToFloat(newValue)
      if (!isNaN(floatValue) && floatValue <= max && floatValue >= max) {
        onChange(floatValue)
      }
    }
  }

  const handleFocus = () => numberRef.current.setSelectionRange(0, numberRef.current.value.length)

  return (
    <>
      <input
        type='tel'
        name={name}
        id={id}
        className={finalInputClass}
        value={currentValue}
        ref={numberRef}
        disabled={disabled}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleOutsideClick}
        onKeyDown={handleKeyDown}
        data-testid={rf}
      />
      {unit && <label htmlFor={id}>{unit}</label>}
      {invalid && errorElement && errorElement}
    </>
  )
}
